import {
  Button,
  ButtonGroup,
  Classes,
  Colors,
  InputGroup,
  Menu,
  MenuItem,
  Spinner,
  Tag,
} from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import { useDebounce } from "ahooks";
import { getIn } from "formik";
import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import {
  FGCustomInput,
  IFGContext,
  IFGCustomInputProps,
  useFGContext,
} from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";

import {
  AzureMemberApi,
  AzureMemberGridDto,
  AzureMemberSearchCriteria,
  SearchQueryOfAzureMemberSearchCriteria,
} from "../../../api";
import {
  useAbortableApiServiceFactory,
  useCommonHooks,
} from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { ClearButton } from "../../applicationButtons";
import { FGReadOnlyInput } from "../../formGenerator/FGReadOnlyInput";

const Container = styled.div<{ height: number }>`
  display: flex;
  flex: 1;
  & .${Classes.INPUT} {
    height: ${(props) => props.height}px;
  }
`;

const StyledTag = styled(Tag)`
  & a {
    color: ${Colors.WHITE};
  }
`;

const NamesContainer = styled.div`
  max-width: 850px;
  > * + * {
    margin-left: 5px;
  }
`;

const DropdownMenu = styled(Menu)`
  max-height: 300px;
  overflow-y: auto;
`;

export interface IUserSelectorFieldProps
  extends Omit<IFGCustomInputProps, "children"> {
  disabled?: boolean;
  allowMultipleSelection?: boolean;
  initialSearch?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  goToSelector?: (ctx?: IFGContext) => void;
  customItem?: {
    displayValue: string;
    onRemove?: () => void;
  };
  loading?: boolean;
  /** Active la recherche rapide (autocomplete) dans le champ. Elle n'est jamais filtrée. */
  quickSearch?: boolean;
}

export const UserSelectorField: React.FunctionComponent<
  IUserSelectorFieldProps
> = ({
  name,
  label = null,
  disabled = false,
  className,
  allowMultipleSelection = false,
  initialSearch = true,
  ref,
  goToSelector,
  customItem,
  loading,
  quickSearch,
  ...fgCustomInputProps
}) => {
  const { apis, t } = useCommonHooks();
  const azureMemberApiFactory = useAbortableApiServiceFactory(AzureMemberApi);
  const lastAbortController = React.useRef<AbortController>();
  const ctx = useFGContext();
  const { formik, editMode } = ctx;

  const value = React.useMemo(
    () => !!formik?.values && getIn(formik?.values, name),
    [formik?.values, name]
  );

  const hasValue = React.useMemo(
    () =>
      (allowMultipleSelection && !isEmpty(value)) ||
      (!allowMultipleSelection && !!value),
    [allowMultipleSelection, value]
  );
  const [currentSelectedIds, setSelectedIds] = React.useState<number[]>([]);

  const { data: rawDisplayNames, isFetching: loadingDisplayNames } = useQuery(
    ["users-display-names", currentSelectedIds],
    async () =>
      !!currentSelectedIds && apis.user.getDisplayNames(currentSelectedIds)
  );

  // Preserve selection order by sorting display names to match formik value order
  const displayNames = React.useMemo(() => {
    if (!rawDisplayNames || !Array.isArray(rawDisplayNames))
      return rawDisplayNames;
    const orderedIds = isArray(value) ? value : value ? [value] : [];
    return [...rawDisplayNames].sort(
      (a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id)
    );
  }, [rawDisplayNames, value]);

  const clearSelection = React.useCallback(() => {
    formik?.setFieldValue(name, allowMultipleSelection ? [] : null, true);
    setSelectedIds([]);
  }, [allowMultipleSelection, formik, name]);

  React.useEffect(() => {
    if (value) {
      setSelectedIds(isArray(value) ? value : [value]);
    } else {
      setSelectedIds([]);
    }
  }, [formik.values, name, value]);

  const removeId = React.useCallback(
    (id: number) => {
      const newIds = [...currentSelectedIds];
      newIds.splice(currentSelectedIds.indexOf(id), 1);
      formik?.setFieldTouched(name);
      formik?.setFieldValue(name, allowMultipleSelection ? newIds : null, true);
      setSelectedIds(newIds);
    },
    [formik, currentSelectedIds, name, allowMultipleSelection]
  );

  const getDisplayTag = React.useCallback(
    (id: number, name: string, tags: boolean) => {
      return tags ? (
        <StyledTag key={id} onRemove={editMode && (() => removeId(id))}>
          {name}
        </StyledTag>
      ) : (
        name
      );
    },
    [editMode, removeId]
  );

  const inputContainerCss = React.useMemo(() => {
    if (allowMultipleSelection) {
      return { padding: "0 0 0 0" };
    } else {
      return { padding: "0.4rem 0 0 0.6rem" };
    }
  }, [allowMultipleSelection]);

  const [tagBoxHeight, setTagBoxHeight] = React.useState(30);
  const tagBoxRef = React.useCallback((node) => {
    if (!node) return;
    const resizeObserver = new ResizeObserver((e, o) => {
      const height = e?.[0]?.target?.clientHeight ?? 30;
      setTagBoxHeight(height > 30 ? height : 30);
    });
    resizeObserver.observe(node);
  }, []);

  // === Autocomplete state ===
  const [searchText, setSearchText] = React.useState("");
  const [isEditing, setIsEditing] = React.useState(false);
  const debouncedSearchText = useDebounce(searchText, { wait: 300 });
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);

  // For single-select: show display name when not editing, search text when editing
  const singleSelectDisplayName = React.useMemo(() => {
    if (!allowMultipleSelection && displayNames?.length > 0) {
      return displayNames[0].text ?? "";
    }
    return "";
  }, [allowMultipleSelection, displayNames]);

  const shouldSearch = !!quickSearch && debouncedSearchText.trim().length >= 3;
  const isTypingPending =
    searchText.length >= 3 && searchText !== debouncedSearchText;

  // Aucun critère implicite : la recherche rapide doit pouvoir trouver n'importe quel membre
  // Azure. Le filtrage se fait dans le sélecteur, où les critères sont visibles et retirables.
  const { data: searchResults, isFetching: isSearching } = useQuery(
    ["user-autocomplete", debouncedSearchText],
    async () => {
      const criteria = AzureMemberSearchCriteria.fromJS({
        fullNameFilter: debouncedSearchText.trim(),
        take: 25,
        skip: 0,
        linkedUserOnly: true,
        activeOnly: true,
      });
      const { api: abortableApi, abortController } = azureMemberApiFactory();
      lastAbortController.current = abortController;
      return abortableApi.baseSearch(
        SearchQueryOfAzureMemberSearchCriteria.fromJS({ criteria })
      );
    },
    { enabled: shouldSearch }
  );
  const onAbort = React.useCallback(
    () => lastAbortController.current?.abort(),
    []
  );

  React.useEffect(() => {
    setActiveIndex(0);
  }, [searchResults]);

  const autocompleteInputRef = React.useRef<HTMLInputElement>(null);

  const selectUser = React.useCallback(
    (user: AzureMemberGridDto) => {
      if (allowMultipleSelection) {
        const currentIds = isArray(value) ? [...value] : [];
        if (!currentIds.includes(user.iduser)) {
          formik?.setFieldValue(name, [...currentIds, user.iduser], true);
        }
      } else {
        formik?.setFieldValue(name, user.iduser, true);
      }
      formik?.setFieldTouched(name);
      setSearchText("");
      setIsDropdownOpen(false);
      if (allowMultipleSelection) {
        setTimeout(() => autocompleteInputRef.current?.focus(), 0);
      } else {
        setIsEditing(false);
      }
    },
    [allowMultipleSelection, formik, name, value]
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      const results = searchResults?.results;
      if (!results?.length || !isDropdownOpen) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (results[activeIndex]) {
          selectUser(results[activeIndex]);
          if (!allowMultipleSelection) {
            (e.target as HTMLElement)?.blur();
          }
        }
      } else if (e.key === "Escape") {
        setSearchText("");
        setIsDropdownOpen(false);
      }
    },
    [
      activeIndex,
      allowMultipleSelection,
      isDropdownOpen,
      searchResults?.results,
      selectUser,
    ]
  );

  const showDropdown = isDropdownOpen && searchText.length >= 3;

  // === Autocomplete mode rendering ===
  if (!!quickSearch && editMode) {
    const dropdownContent = (
      <DropdownMenu>
        {isSearching || isTypingPending ? (
          <MenuItem
            icon={<Spinner size={16} />}
            text={t(ETLCodes.GeneralLoading)}
            disabled
          />
        ) : !searchResults?.results?.length ? (
          <MenuItem text={t(ETLCodes.AucunResultat)} disabled icon="zoom-out" />
        ) : (
          searchResults.results.map((user, index) => (
            <MenuItem
              key={user.iduser}
              active={index === activeIndex}
              text={`${user.lastName} ${user.firstName}`}
              label={user.fonction}
              onClick={() => selectUser(user)}
            />
          ))
        )}
      </DropdownMenu>
    );

    const inputValue = allowMultipleSelection
      ? searchText
      : isEditing
      ? searchText
      : loadingDisplayNames || loading
      ? ""
      : singleSelectDisplayName;

    const tagsElement =
      allowMultipleSelection && (loadingDisplayNames || loading) ? (
        <Spinner size={16} style={{ margin: "7px" }} />
      ) : allowMultipleSelection ? (
        <NamesContainer
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "3px",
            paddingLeft: "4px",
          }}
          ref={tagBoxRef}
        >
          {!!customItem && (
            <StyledTag onRemove={customItem.onRemove}>
              {customItem.displayValue}
            </StyledTag>
          )}
          {displayNames?.map((dn) => (
            <StyledTag key={dn.id} onRemove={() => removeId(dn.id)}>
              {dn.text}
            </StyledTag>
          ))}
        </NamesContainer>
      ) : loadingDisplayNames || loading ? (
        <Spinner size={16} style={{ margin: "7px" }} />
      ) : undefined;

    return (
      <Container height={tagBoxHeight}>
        <FGCustomInput name={name} label={label} {...fgCustomInputProps}>
          {() => (
            <div style={{ width: "100%" }}>
              <Popover2
                isOpen={showDropdown}
                minimal
                position="bottom-left"
                autoFocus={false}
                matchTargetWidth={true}
                fill={true}
                onInteraction={(nextOpenState) => {
                  if (!nextOpenState && !isDropdownOpen) return;
                }}
                content={dropdownContent}
              >
                <InputGroup
                  fill={true}
                  inputRef={autocompleteInputRef}
                  value={inputValue}
                  onChange={(e) => {
                    if (!!onAbort) onAbort();
                    setSearchText(e.target.value);
                    setIsEditing(true);
                    if (e.target.value.length >= 3) {
                      setIsDropdownOpen(true);
                    } else {
                      setIsDropdownOpen(false);
                    }
                  }}
                  onFocus={() => {
                    setIsEditing(true);
                    if (!allowMultipleSelection) {
                      setSearchText("");
                    }
                    if (searchText.length >= 3) {
                      setIsDropdownOpen(true);
                    }
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      if (!!onAbort) onAbort();
                      setIsEditing(false);
                      setIsDropdownOpen(false);
                      setSearchText("");
                    }, 200);
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder={t(ETLCodes.Search)}
                  disabled={disabled || loading}
                  leftElement={tagsElement}
                  rightElement={
                    <ButtonGroup>
                      {hasValue && (
                        <ClearButton
                          onClick={() => {
                            clearSelection();
                            setSearchText("");
                            setIsEditing(false);
                          }}
                          tabIndex={-1}
                          disabled={disabled}
                        />
                      )}
                      <Button
                        icon="people"
                        minimal={true}
                        onClick={() => goToSelector(ctx)}
                        disabled={disabled}
                        tabIndex={-1}
                      />
                    </ButtonGroup>
                  }
                />
              </Popover2>
            </div>
          )}
        </FGCustomInput>
      </Container>
    );
  }

  // === Default readonly rendering ===
  return (
    <Container height={tagBoxHeight}>
      <FGReadOnlyInput
        name={name}
        label={label}
        readonly={!editMode}
        leftElement={
          <div style={inputContainerCss} ref={tagBoxRef}>
            {loadingDisplayNames || loading ? (
              <Spinner size={16} />
            ) : (
              <NamesContainer>
                {displayNames.map((dn) =>
                  getDisplayTag(dn.id, dn.text, allowMultipleSelection)
                )}
                {!!customItem && allowMultipleSelection && (
                  <StyledTag onRemove={editMode && customItem.onRemove}>
                    {customItem.displayValue}
                  </StyledTag>
                )}
              </NamesContainer>
            )}
          </div>
        }
        rightElement={
          editMode && (
            <ButtonGroup>
              {hasValue && (
                <ClearButton
                  onClick={clearSelection}
                  tabIndex={-1}
                  disabled={disabled}
                />
              )}
              <Button
                icon="people"
                minimal={true}
                onClick={() => goToSelector(ctx)}
                disabled={disabled}
                tabIndex={-1}
              />
            </ButtonGroup>
          )
        }
        {...fgCustomInputProps}
      />
    </Container>
  );
};
